'use client';

import Icon from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { App, Divider, Popover, message } from 'antd';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import DeleteSvg from '@/components/Icon/svgs/delete.svg';
import MoreSvg from '@/components/Icon/svgs/more.svg';
import ShareSvg from '@/components/Icon/svgs/share_pop.svg';
import { useTranslation } from '@/i18n/client';
import { deleteNote } from '@/service/note';
import type { INote, INote_List } from '@/service/note';
import useNoteItemStore from '@/store/useNoteItemStore';
import reportExpose, { EReportExposeKey } from '@/utils/report/reportExpose';

import ShareModal from '../components/ShareModal';
import styles from './index.less';

interface IProps {
  className?: string;
  detail: INote | INote_List;
  mindId?: string;
  onDeleteCb?: () => void;
  type?: 'note' | 'zoom_in';
  [key: string]: any;
}

const NoteSettings = (props: IProps) => {
  const { detail, onDeleteCb, mindId, className, type = 'note' } = props;
  const { modal } = App.useApp();
  const [openShareModal, setOpenShareModal] = useState<boolean>(false);
  const { t } = useTranslation();

  const addDeleteItemId = useNoteItemStore((state) => state.addDeleteItemId);
  const updateNote = useNoteItemStore((state) => state.updateNote);

  // 状态回写
  useEffect(() => {
    return () => {
      if (type === 'zoom_in' && detail.noteId) {
        updateNote(detail.noteId);
      }
    };
  }, [detail.noteId, type, updateNote]);

  const { run: runDelete } = useRequest(deleteNote, {
    manual: true,
    onSuccess: (res) => {
      if (res.data.code === 0) {
        message.success(t('common.deleteSuccess'));
        detail.noteId && addDeleteItemId(Number(detail.noteId));
        onDeleteCb?.();
      } else {
        message.error(res.data.message || t('common.deleteFail'));
      }
    },
    onError: (err) => {
      message.error(err.message || t('common.deleteFail'));
    }
  });

  const renderPopoverContent = () => {
    // 只有list里的能够触发其他操作
    const _detail = detail as INote_List;

    return (
      <div
        className={classNames(styles.popoverContentContainer)}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div
          className={classNames(styles.itemContainer, {
            [styles.canHover]: true
          })}
          onClick={() => {
            reportExpose(EReportExposeKey.sharing_evoked_expose, { key1: detail.type });
            setOpenShareModal(true);
          }}
        >
          <Icon className={styles.icon} component={ShareSvg} />
          <span>{t('memoryDetail.shareNote')}</span>
        </div>

        <Divider className={styles.divider} />

        <div
          className={classNames(styles.itemContainer, { [styles.canHover]: true })}
          onClick={() => {
            modal.confirm({
              title: t('common.sureToDelete'),
              onOk: () => {
                if (!_detail.noteId) {
                  message.error(t('error.oops'));

                  return;
                }

                runDelete({ noteId: _detail.noteId, mindId: mindId || _detail.mindId });
              },
              okText: t('common.delete'),
              cancelText: t('common.cancel')
            });
          }}
        >
          <Icon className={styles.icon} component={DeleteSvg} />
          <span>{t('common.delete')}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Popover
        arrow={false}
        content={renderPopoverContent()}
        getPopupContainer={(e) => e.parentNode as HTMLElement}
        overlayClassName={styles.overlayClassName}
        placement="bottomLeft"
        trigger={['hover']}
      >
        <Icon
          className={classNames(
            'w-6 h-6 p-[3px] text-primary-black text-base box-border border border-solid border-[#eee9e4] shadow-[0_4px_4px_0_rgba(0,0,0,0.08)] bg-primary-white rounded-[30px] ',
            className
          )}
          component={MoreSvg}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </Popover>

      <div className="absolute" onClick={(e) => e.stopPropagation()}>
        <ShareModal
          detail={detail}
          mindId={mindId || ''}
          onClose={() => {
            setOpenShareModal(false);
          }}
          open={openShareModal}
        />
      </div>
    </>
  );
};

export default NoteSettings;
